'use client'

/**
 * CompoundingCalculator: free tool for "trading compounding calculator / grow a small account". Projects an
 * account's expected growth when you risk a fixed % of the (growing) balance each trade at a given win rate and
 * reward-to-risk, over a chosen trade frequency and horizon. Aspirational + honest: shows a negative edge
 * shrinking the account, and states plainly that it is an illustration, not advice. Pure client math.
 */

import { useMemo, useState } from 'react'
import CalcField from '@/components/calc/CalcField'

const fmtUsd = (n: number): string => {
  if (!isFinite(n)) return '$0'
  const abs = Math.abs(n)
  if (abs >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`
  if (abs >= 10_000) return `$${(n / 1000).toFixed(1)}K`
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`
}
const fmtUsdFull = (n: number): string => (isFinite(n) ? `$${n.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : '$0')

export default function CompoundingCalculator() {
  const [start, setStart] = useState('500')
  const [riskPct, setRiskPct] = useState('2')
  const [rr, setRr] = useState('2')
  const [winRate, setWinRate] = useState('45')
  const [tradesPerWeek, setTradesPerWeek] = useState('5')
  const [weeks, setWeeks] = useState('26')

  const r = useMemo(() => {
    const s0 = parseFloat(start) || 0
    const risk = (parseFloat(riskPct) || 0) / 100
    const R = parseFloat(rr) || 0
    const wr = (parseFloat(winRate) || 0) / 100
    const tpw = Math.max(0, Math.floor(parseFloat(tradesPerWeek) || 0))
    const wk = Math.max(0, Math.floor(parseFloat(weeks) || 0))
    // Expected fractional return per trade, risking a fixed % of the (growing) account each trade:
    // e = risk% * (winRate * RewardMultiple - lossRate). Position size scales with the account, so it compounds.
    const ePerTrade = risk * (wr * R - (1 - wr))
    const N = tpw * wk
    const final = s0 * Math.pow(1 + ePerTrade, N)
    const totalPct = s0 > 0 ? (final / s0 - 1) * 100 : 0
    const multiple = s0 > 0 ? final / s0 : 0
    // Milestone curve: balance at 6 evenly spaced weeks for a simple growth visual.
    const points: { week: number; bal: number }[] = []
    const steps = 6
    for (let i = 1; i <= steps; i++) {
      const w = Math.round((wk / steps) * i)
      points.push({ week: w, bal: s0 * Math.pow(1 + ePerTrade, tpw * w) })
    }
    const negative = ePerTrade <= 0
    const invalid = s0 <= 0 || N <= 0
    return { s0, ePerTrade, N, final, totalPct, multiple, points, negative, invalid }
  }, [start, riskPct, rr, winRate, tradesPerWeek, weeks])

  const accent = r.negative ? '#f43f5e' : '#34d399'
  const maxBal = Math.max(...r.points.map((p) => p.bal), r.s0, 1)

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 sm:p-6">
      <div className="grid gap-4 sm:grid-cols-3">
        <CalcField label="Starting balance" value={start} onChange={setStart} prefix="$" step="50" hint="What you start with" />
        <CalcField label="Risk per trade" value={riskPct} onChange={setRiskPct} suffix="%" step="0.5" hint="% of the account per trade" />
        <CalcField label="Reward : risk" value={rr} onChange={setRr} suffix="R" step="0.5" hint="Avg win size vs your risk" />
      </div>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <CalcField label="Win rate" value={winRate} onChange={setWinRate} suffix="%" step="1" hint="How often you win" />
        <CalcField label="Trades per week" value={tradesPerWeek} onChange={setTradesPerWeek} step="1" hint="Your trade frequency" />
        <CalcField label="Time horizon" value={weeks} onChange={setWeeks} suffix="wk" step="1" hint="Number of weeks" />
      </div>

      {r.invalid ? (
        <div className="mt-5 rounded-xl border border-zinc-700/70 bg-zinc-900/60 px-4 py-6 text-center text-sm text-zinc-400">
          Enter a starting balance and a time horizon to project your account growth.
        </div>
      ) : (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border px-5 py-4" style={{ borderColor: `${accent}4d`, background: `${accent}12` }}>
              <div className="text-[12px] font-bold uppercase tracking-wider" style={{ color: accent }}>Projected balance</div>
              <div className="mt-1 text-[34px] font-black leading-none tabular-nums text-white">{fmtUsdFull(r.final)}</div>
              <div className="mt-1.5 text-[13px] text-zinc-400">after {r.N.toLocaleString()} trades ({weeks} weeks)</div>
            </div>
            <div className="rounded-2xl border border-zinc-700/70 bg-zinc-900/60 px-5 py-4">
              <div className="text-[12px] font-bold uppercase tracking-wider text-zinc-400">Total return</div>
              <div className={`mt-1 text-[34px] font-black leading-none tabular-nums ${r.negative ? 'text-rose-400' : 'text-emerald-400'}`}>{r.totalPct >= 0 ? '+' : ''}{r.totalPct.toFixed(0)}%</div>
              <div className="mt-1.5 text-[13px] text-zinc-400">{r.multiple >= 1 ? `${r.multiple.toFixed(1)}x your starting balance` : `${(r.multiple * 100).toFixed(0)}% of your start (account shrank)`}</div>
            </div>
          </div>

          {r.negative && (
            <div className="mt-3 rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-[13px] text-rose-200">
              With these inputs your edge is negative (expected {(r.ePerTrade * 100).toFixed(2)}% per trade), so the account shrinks over time. Raise your win rate or your reward-to-risk until the per-trade expectancy turns positive.
            </div>
          )}

          {/* Growth curve: 6 milestone bars, aspirational + shareable */}
          <div className="mt-4 rounded-2xl border border-zinc-700/70 bg-zinc-900/60 p-4">
            <div className="mb-3 text-[12px] font-bold uppercase tracking-wider text-zinc-400">Account growth</div>
            <div className="flex items-end gap-1.5" style={{ height: 96 }}>
              {r.points.map((p, i) => (
                <div key={i} className="flex flex-1 flex-col items-center gap-1">
                  <div className="w-full rounded-t" style={{ height: `${Math.max(4, (p.bal / maxBal) * 80)}px`, background: accent, opacity: 0.35 + (i / r.points.length) * 0.6 }} title={`Week ${p.week}: ${fmtUsdFull(p.bal)}`} />
                  <div className="text-[9px] tabular-nums text-zinc-500">w{p.week}</div>
                </div>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-zinc-400">
              {r.points.map((p, i) => (
                <span key={i} className="tabular-nums"><span className="text-zinc-600">w{p.week}</span> {fmtUsd(p.bal)}</span>
              ))}
            </div>
          </div>

          <p className="mt-3 text-[11px] leading-snug text-zinc-500">
            This projects the expected path if you risk a fixed percentage of the account each trade at the win rate and reward-to-risk you enter. Real results vary trade to trade, drawdowns happen, and past performance never guarantees future results. It is an illustration, not investment advice.
          </p>
        </>
      )}
    </div>
  )
}
